<script lang="ts">
	import ContentBlocks from "@components/ContentBlocks.svelte";
	import HugeHeading from "@components/HugeHeading.svelte";
	import InquiryModalForm from "@components/InquiryModalForm.svelte";
	import ProductDetailGallery from "@components/ProductDetailGallery.svelte";
	import ProductStructuredData from "@components/ProductStructuredData.svelte";
	import Button from "@components/common/Button.svelte";
	import CkEditorContent from "@components/common/CkEditorContent.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { iconMap } from "@core/assets/icons/iconMap.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { getMoreAboutProductAnchor } from "@core/schema/paths/getMoreAboutProductAnchor.js";
	import { getPartnersGoldDetailPath } from "@core/schema/paths/getPartnersGoldDetailPath.js";
	import { getPrimaryImageOfProduct } from "@core/schema/utils/getPrimaryImageOfProduct.js";
	import { getProductBenefitsHeading } from "@core/schema/utils/getProductBenefitsHeading.js";
	import type { ProductsCatalogueDetailProps } from "./ProductsCatalogueDetailProps.js";

	const { product, tenant }: ProductsCatalogueDetailProps = $props();
	let showInquiryForm = $state(false);
</script>

<ProductStructuredData {product} />

<Layout ogImage={getPrimaryImageOfProduct(product)}>
	<section class="pt-8 pb-8 md:pb-16 lg:pb-20 full-width max-w-[87.5rem] sm:mx-auto">
		<HugeHeading level={1} text={product.name} class="lg:!text-9xl text-center mild" />

		<div class="flex flex-col xl:flex-row gap-8 md:gap-16 mx-3">
			<div class="flex flex-col mt-5 xl:mt-20 order-2 xl:order-1 mx-3 xl:mx-0">
				<div>
					<div>
						<p class="text-[1.25rem] md:text-xl lg:text-[2rem] font-semibold text-cmcsecondary mb-4">
							<Formatted text={product.perex} />
						</p>
						<CkEditorContent lightboxId={`product-description-${product.id}`} html={product.description} />
					</div>
					<div class="mt-6 md:mt-12">
						<Button
							onclick={(): void => {
								showInquiryForm = true;
							}}
							variant={ButtonVariant.Primary}
							icon="arrow"
						>
							<Formatted text="Nezávazně poptat" />
						</Button>
					</div>
				</div>

				<div class="flex flex-col mt-12 md:mt-24 gap-2">
					{#if product.company}
						<div class="flex rounded-3xl max-w-[42rem] bg-white/50 backdrop-blur-[8px] p-4 gap-8">
							<div class="font-medium uppercase text-xs/4 md:text-base"><Formatted text="Partner" /></div>
							<div class="flex-1 font-bold text-xs/4 md:text-base text-right">
								{#if product.company.hasDetail}
									<Formatted text="l[{getPartnersGoldDetailPath(tenant, product.company)}][[{product.company.name}]]" />
								{:else}
									<span><Formatted text={product.company.name} /></span>
								{/if}
							</div>
						</div>
					{/if}
					<div class="flex rounded-3xl max-w-[42rem] bg-white/50 backdrop-blur-[8px] p-4 gap-8">
						<div class="font-medium uppercase text-xs/4 md:text-base"><Formatted text="Kategorie" /></div>
						<div class="flex-1 font-semibold text-xs/4 md:text-base text-right">
							<span><Formatted text={product.categories.map(({ name }) => name).join(", ")} /></span>
						</div>
					</div>
				</div>
			</div>

			<div class="mx-0 md:mx-auto xl:mx-0 order-1 xl:order-2">
				<ProductDetailGallery {product} />
			</div>
		</div>

		{#if product.content}
			<div id={getMoreAboutProductAnchor(tenant)} class="mt-12 md:mt-24 flex flex-col">
				<div>
					<ContentBlocks blocks={product.content} productId={product.id} lightboxIdPerBlock align="left" />
				</div>
			</div>
		{/if}
	</section>

	{#if product.benefits.length > 0}
		<section class="full-width my-8 md:my-16 lg:my-20 mx-3 md:mx-8 2k:mx-auto 2k:w-[116rem]">
			<HugeHeading level={2} class=" md:w-[80%]" text={`stroke[[${getProductBenefitsHeading(product)}]]`} />

			<div
				class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 justify-center items-stretch -mt-3 md:-mt-4 xl:-mt-12 xl:mx-64"
			>
				{#each product.benefits as benefit, i (i)}
					{@const icon = benefit.icon && iconMap[benefit.icon]}
					<div class="bg-white/50 backdrop-blur-[8px] rounded-3xl p-4 gap-8">
						<div class="mb-8 bg-cmcprimary size-16 rounded-2xl flex items-center justify-center">
							{#if icon}
								<Icon {icon} class="size-8 text-white" />
							{/if}
						</div>
						<div>
							<p class="font-medium leading-[1.6]">
								{benefit.text}
							</p>
						</div>
					</div>
				{/each}
			</div>
		</section>
	{/if}

	{#if product.params.length > 0}
		<section class="full-width my-8 md:my-16 lg:my-20 mx-3 md:mx-8 2k:mx-auto 2k:w-[116rem]">
			<HugeHeading level={2} text="Klíčové údaje o produktu" />

			<div class="lg:max-w-[50rem] xl:max-w-[84rem] mx-auto -mt-3 md:-mt-4 xl:-mt-10 mb-8 md:mb-16">
				{#each product.params as param, index (index)}
					{@const icon = param.icon && iconMap[param.icon]}
					<div class="min-h-20 bg-white/50 backdrop-blur-[8px] rounded-3xl p-4 flex items-center mb-2 gap-4">
						{#if icon}
							<Icon {icon} class="text-cmcwarning size-8" />
						{/if}
						<p class="font-medium text-sm/4 md:text-base uppercase">
							<Formatted text={param.name} />
						</p>
						<p class="flex-1 font-semibold text-xs/4 md:text-base text-right">
							{#if param.value}
								<Formatted text={param.value} />
							{:else}
								<span class="font-semibold">-</span>
							{/if}
						</p>
					</div>
				{/each}
			</div>
			<div class="text-center">
				<Button
					onclick={(): void => {
						showInquiryForm = true;
					}}
					variant={ButtonVariant.Outlined}
					icon="arrow"
				>
					<Formatted text="Nezávazně poptat" />
				</Button>
			</div>
		</section>
	{/if}

	<InquiryModalForm bind:showInquiryForm productId={product.id} />
</Layout>
